import React, { useEffect, useMemo, useState } from 'react';
import './AiSignalsPage.scss';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { INTEGRATIONS } from '../../shared/routes/path';
import {
  Brand24Service,
  Brand24ConnectionStatus,
  Brand24BrandInput,
  Brand24ProjectMetric,
  Brand24ContextWord,
  Brand24DiscussionTopic,
  Brand24AiSourceModel,
  Brand24Backlink,
  Brand24SocialSource,
  Brand24MentionsScore,
} from '../../shared/services/integrations/Brand24Service';
import Brand24ProjectConfigDialog from '../../shared/components/integrations/Brand24ProjectConfigDialog';
import LineChart from '../../shared/components/chart/line-chart/LineChart';
import SimpleBar from 'simplebar-react';
import CustomTooltip from '../../shared/components/common/tooltip/CustomTooltip';
import { Tooltip } from 'primereact/tooltip';
import { DataTable } from '../../shared/components/common/data-table/DataTable';
import useIsMobile from '../../shared/hooks/global/UseIsMobile';
import MobilePageHeader from '../../shared/components/common/mobile-menu/MobilePageHeader';
import { Column } from 'primereact/column';
import { Skeleton } from 'primereact/skeleton';
import { format } from 'date-fns';
import FilterPanel from '../../shared/components/filter-panel/FilterPanel';
import { useFilters } from '../../shared/contexts/filters/FiltersContext';
import Title from '../../shared/components/title/Title';
import { updateTag, getTags, deleteTag } from '../../shared/api/services/tags/TagsService';
import { KeywordsData, TagsResponse } from '../../shared/models/tags/TagsResponse';
import { TrendTag } from '../projects/preview/components/trend-tag/TrendTag';

const FEATURES: { icon: string; titleKey: string; descKey: string }[] = [
  {
    icon: 'ico--puzzle',
    titleKey: 'aiSignals.features.integration.title',
    descKey: 'aiSignals.features.integration.desc',
  },
  {
    icon: 'ico--message-square',
    titleKey: 'aiSignals.features.topics.title',
    descKey: 'aiSignals.features.topics.desc',
  },
  {
    icon: 'ico--bell',
    titleKey: 'aiSignals.features.anomaly.title',
    descKey: 'aiSignals.features.anomaly.desc',
  },
];

const ACTIVATION_STEPS = [
  'aiSignals.activation.step1',
  'aiSignals.activation.step2',
  'aiSignals.activation.step3',
  'aiSignals.activation.step4',
];

const STEP_DURATION_MS = 1500;

type SentimentTone = 'positive' | 'neutral' | 'negative';

const MENTIONS_SCORE_GREEN_THRESHOLD = 80;


// Display name + icon per AI model, keyed by the ChatType the backend returns.
const MODEL_META: Record<string, { name: string; icon: string }> = {
  GPT: { name: 'GPT', icon: 'ico--gpt-bck' },
  GEMINI: { name: 'Gemini', icon: 'ico--gemini-bck' },
  CLAUDE: { name: 'Claude', icon: 'ico--claude-bck' },
  PERPLEXITY: { name: 'Perplexity', icon: 'ico--perplexity-bck' },
  DEEP_SEEK: { name: 'DeepSeek', icon: 'ico--deepseek-bck' },
  GROK: { name: 'Grok', icon: 'ico--grok-bck' },
};

// Compact number for reach (e.g. 123810442 → "124M").
const formatCompact = (n: number): string =>
  new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(n || 0);

// The AI-coverage sections (Top AI Source Domains / Top Sources / AI Backlinks) depend on a heavy ChatBeat
// DB query that can transiently fail OR return empty under load, blanking the section until a refresh.
// Retry a bounded number of times on an error or an empty result before accepting it.
// Retry a fetch until it reports data (the backend throws/returns empty on transient DB failures under the
// AI Signals page's concurrent load — a retry gives it another shot instead of blanking the section).
const fetchWithRetry = async <T,>(
  fn: () => Promise<T>,
  hasData: (result: T) => boolean,
  attempts = 4,
  delayMs = 900
): Promise<T | undefined> => {
  let result: T | undefined;
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      result = await fn();
      if (hasData(result)) return result;
    } catch {
      // fall through and retry
    }
    if (attempt < attempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
  return result;
};

// Word cloud for "Context of a discussion". weight (1–6) drives font size and the default blue/grey
// coloring; sentiment drives coloring when the "Show sentiment" toggle is on.
const WORD_FONT_SIZE: Record<number, number> = { 6: 40, 5: 28, 4: 20, 3: 17, 2: 14, 1: 12 };
const WORD_GREY: Record<number, string> = {
  4: 'var(--grey-ux-700)',
  3: 'var(--grey-ux-600)',
  2: 'var(--grey-ux-500)',
  1: 'var(--grey-ux-400)',
};

// Shimmer placeholder shown in each Brand24 section while its data loads. `bordered` keeps the skeleton's
// own frame — use it where the real content is itself framed (a bordered DataTable); leave it off where a
// parent box already draws the frame (e.g. the Mentions stats-box) to avoid a frame-in-a-frame look.
const SectionSkeleton: React.FC<{ bordered?: boolean; bare?: boolean }> = ({ bordered, bare }) => (
  <Skeleton
    className={[
      'skeleton--table',
      'skeleton--table-4',
      bordered ? '' : 'ai-signals-skeleton',
      // `bare` drops the skeleton's own padding for sections whose container already pads it (e.g. the
      // chart's stats-box__content), avoiding doubled padding.
      bare ? 'ai-signals-skeleton--bare' : '',
    ]
      .filter(Boolean)
      .join(' ')}
  >
    <span />
    <span />
    <span />
    <span />
  </Skeleton>
);

// Shimmer placeholder for a section title while its data loads (37px tall to match the real title row so
// the frame below doesn't shift on load).
const TitleSkeleton: React.FC = () => <span className="ai-signals-title-skeleton" />;

const getWordColor = (
  weight: number,
  sentiment: SentimentTone,
  showSentiment: boolean
): string => {
  if (showSentiment) {
    if (sentiment === 'positive') return 'var(--success-ux-500)';
    if (sentiment === 'negative') return 'var(--error-ux-500)';
    return 'var(--grey-ux-500)';
  }
  return weight >= 5 ? 'var(--primary-ux-400)' : WORD_GREY[weight] ?? 'var(--grey-ux-500)';
};


const AiSignalsPage: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams();

  const [isActivating, setIsActivating] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [isActivated, setIsActivated] = useState(false);
  const [showSentiment, setShowSentiment] = useState(false);
  const [brand24Status, setBrand24Status] = useState<Brand24ConnectionStatus | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [isProjectLinked, setIsProjectLinked] = useState(false);
  // The backend configureProject call runs during the activation animation; the results are only
  // revealed once it has actually succeeded (so a failed link never shows the charts).
  const [configReady, setConfigReady] = useState(false);
  // Real Brand24 metrics (AI Backlinks + sentiment) for the linked projects — the competitors table.
  const [competitorMetrics, setCompetitorMetrics] = useState<Brand24ProjectMetric[]>([]);
  // "Context of a discussion" word cloud for the main brand (words from its mentions).
  const [contextWords, setContextWords] = useState<Brand24ContextWord[]>([]);
  // "Discussion Topics" for the main brand (Brand24 topics).
  const [discussionTopics, setDiscussionTopics] = useState<Brand24DiscussionTopic[]>([]);
  // "Mentions in AI Sources - Trend": per-day counts per brand as a chart matrix.
  const [trendMatrix, setTrendMatrix] = useState<(string | number)[][]>([]);
  // "Top AI Source Domains": per model, AI-cited domains Brand24 also mentions the brand on.
  const [sourceDomains, setSourceDomains] = useState<Brand24AiSourceModel[]>([]);
  // "AI Backlinks": overlap domains with Brand24 mentions/reach + citing models + sentiment.
  const [backlinks, setBacklinks] = useState<Brand24Backlink[]>([]);
  // Social source tiles (FB / Instagram / X) with live connection status (OWN_API_KEY mode only).
  const [socialSources, setSocialSources] = useState<Brand24SocialSource[]>([]);
  // Headline "Mentions in AI Sources" stat (AI-source coverage score + mentions trend).
  const [mentionsScore, setMentionsScore] = useState<Brand24MentionsScore>({ score: 0, total: 0, trend: 0 });
  // True while the section data is being fetched (drives the skeletons).
  const [isFetchingData, setIsFetchingData] = useState(false);
  // True until the initial connection/link check resolves — avoids flashing the "connect" card when the
  // project is actually already linked.
  const [checkingLink, setCheckingLink] = useState(true);
  // The project's ChatBeat tags — a topic is "connected" when a tag shares its label (toggle add/remove).
  const [projectTags, setProjectTags] = useState<TagsResponse[]>([]);
  // Date range comes from the shared FilterPanel (FiltersContext); drives every section's query.
  const { range } = useFilters();
  const from = range?.[0] ? format(range[0], 'yyyy-MM-dd') : undefined;
  const to = range?.[1] ? format(range[1], 'yyyy-MM-dd') : undefined;
  // Dates are undefined while FiltersContext hydrates (waiting for /date-range), so the fetch effect
  // cannot start yet — treat that window as loading or the sections flash empty first.
  const loadingData = isFetchingData || !from || !to;

  useEffect(() => {
    setCheckingLink(true);
    Promise.allSettled([
      Brand24Service.getConnectionStatus()
        .then(setBrand24Status)
        .catch((error) => console.error('Error checking Brand24 connection:', error)),
      Brand24Service.getProjectConfig(Number(projectId))
        .then((config) => setIsProjectLinked((config?.brands?.length ?? 0) > 0))
        .catch((error) => console.error('Error checking Brand24 link:', error)),
    ]).finally(() => setCheckingLink(false));
  }, [projectId]);

  // Once the project is linked (or a just-finished activation), pull the live AI Backlinks + sentiment
  // metrics for the competitors table.
  useEffect(() => {
    if ((!isProjectLinked && !isActivated) || !from || !to) return;
    // Ignore results from a superseded run (e.g. the date range changed mid-load) so a slow earlier
    // request can't overwrite the current one with stale/empty data.
    let cancelled = false;
    const guard =
      <T,>(setter: (value: T) => void) =>
      (value: T) => {
        if (!cancelled) setter(value);
      };
    setIsFetchingData(true);
    Promise.allSettled([
      Brand24Service.getProjectMetrics(Number(projectId), from, to)
        .then(guard(setCompetitorMetrics))
        .catch((error) => console.error('Error loading Brand24 metrics:', error)),
      Brand24Service.getBrandContext(Number(projectId), from, to)
        .then(guard(setContextWords))
        .catch((error) => console.error('Error loading Brand24 context:', error)),
      Brand24Service.getDiscussionTopics(Number(projectId), from, to)
        .then(guard(setDiscussionTopics))
        .catch((error) => console.error('Error loading Brand24 topics:', error)),
      Brand24Service.getMentionsTrend(Number(projectId), from, to)
        .then(guard(setTrendMatrix))
        .catch((error) => console.error('Error loading Brand24 trend:', error)),
      fetchWithRetry(
        () => Brand24Service.getAiSourceOverview(Number(projectId), from, to),
        (r) => r.sourceDomains.length > 0 || r.backlinks.length > 0
      )
        .then((overview) => {
          if (!overview || cancelled) return;
          setSourceDomains(overview.sourceDomains);
          setBacklinks(overview.backlinks);
        })
        .catch((error) => console.error('Error loading Brand24 AI sources:', error)),
      Brand24Service.getSocialSources(Number(projectId), from, to)
        .then(guard(setSocialSources))
        .catch((error) => console.error('Error loading Brand24 social sources:', error)),
      Brand24Service.getMentionsScore(Number(projectId), from, to)
        .then(guard(setMentionsScore))
        .catch((error) => console.error('Error loading Brand24 mentions score:', error)),
    ]).finally(() => {
      if (!cancelled) setIsFetchingData(false);
    });
    return () => {
      cancelled = true;
    };
  }, [projectId, isProjectLinked, isActivated, from, to]);

  // Load the project's tags so the "Discussion Topics" rows know which topics are already tagged.
  useEffect(() => {
    if (!isProjectLinked && !isActivated) return;
    getTags(Number(projectId))
      .then(setProjectTags)
      .catch((error) => console.error('Error loading tags:', error));
  }, [projectId, isProjectLinked, isActivated]);

  // Price + connect flow are driven by whether the Brand24 integration is connected, not by the
  // addon: a disconnected integration shows the price and lets the user add (connect) it.
  const isBrand24Connected = brand24Status?.isConnected ?? false;
  // OWN_API_KEY: let the user pick one of THEIR Brand24 projects. DEFAULT keeps the built-in flow.
  const isOwnMode = brand24Status?.mode === 'OWN_API_KEY';

  const handleUnlink = async () => {
    await Brand24Service.unlinkProject(Number(projectId));
    setIsProjectLinked(false);
    setIsActivated(false);
    setIsActivating(false);
    setActiveStep(0);
    setConfigReady(false);
  };

  const reloadTags = () => {
    getTags(Number(projectId))
      .then(setProjectTags)
      .catch((error) => console.error('Error loading tags:', error));
  };

  // Adds a Brand24 discussion topic to the project's ChatBeat tags (OWN_API_KEY mode only).
  const handleAddTopicToTag = async (topicName: string) => {
    try {
      await updateTag({
        projectId: Number(projectId),
        label: topicName,
        keywordsData: [{ normalizedKeyword: topicName.toLowerCase().trim() } as KeywordsData],
      });
      reloadTags();
    } catch (error) {
      console.error('Error adding topic to tags:', error);
    }
  };

  // Removes the ChatBeat tag connected to a discussion topic.
  const handleRemoveTopicTag = async (tagId: number) => {
    try {
      await deleteTag(tagId, Number(projectId));
      reloadTags();
    } catch (error) {
      console.error('Error removing topic tag:', error);
    }
  };

  // Kick off the activation animation and configure the Brand24 projects concurrently. The animation is
  // the loading indicator; the results are gated on the API succeeding (see the effect below).
  const handleConfigure = (brands: Brand24BrandInput[]) => {
    setConfigReady(false);
    setActiveStep(0);
    setIsActivating(true);
    Brand24Service.configureProject(Number(projectId), brands)
      .then(() => {
        setIsProjectLinked(true);
        setConfigReady(true);
      })
      .catch((error) => {
        console.error('Error configuring Brand24 projects:', error);
        setIsActivating(false);
        setActiveStep(0);
      });
  };

  // Advance one step per tick until every step is complete, then — once the backend link has succeeded —
  // reveal the results section.
  useEffect(() => {
    if (!isActivating) {
      return;
    }
    if (activeStep < ACTIVATION_STEPS.length) {
      const timer = setTimeout(() => setActiveStep((step) => step + 1), STEP_DURATION_MS);
      return () => clearTimeout(timer);
    }
    if (!configReady) {
      return;
    }
    const timer = setTimeout(() => setIsActivated(true), 800);
    return () => clearTimeout(timer);
  }, [isActivating, activeStep, configReady]);

  const renderSentiment = (tone: SentimentTone) => (
    <span className={`ai-signals-sentiment ai-signals-sentiment--${tone}`}>
      {t(`aiSignals.results.sentiment.${tone}`)}
    </span>
  );

  // "Mentions per Model - Top Sources": the same common-source data as "Top AI Source Domains", pivoted
  // to be domain-centric (each domain with its per-model citation counts).
  const topSources = useMemo(() => {
    const models = sourceDomains.map((s) => ({
      model: s.model,
      ...(MODEL_META[s.model] ?? { name: s.model, icon: '' }),
    }));
    const domainCounts = new Map<string, number[]>();
    sourceDomains.forEach((entry, modelIndex) => {
      entry.domains.forEach((d) => {
        let counts = domainCounts.get(d.domain);
        if (!counts) {
          counts = new Array(sourceDomains.length).fill(0);
          domainCounts.set(d.domain, counts);
        }
        counts[modelIndex] = d.count;
      });
    });
    const rows = Array.from(domainCounts.entries())
      .map(([domain, counts]) => ({ domain, counts, total: counts.reduce((a, b) => a + b, 0) }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 8);
    return { models, rows };
  }, [sourceDomains]);

  const renderResults = () => (
    <div className="ai-signals-results">
      {isOwnMode && (loadingData || socialSources.length > 0) && (
        <div className="tiles tiles--page ai-signals-results__tiles">
          {loadingData
            ? [0, 1, 2].map((i) => (
                <div key={i} className="tile-box">
                  <div className="tile-box__box ai-signals-tile-skeleton">
                    <span className="ai-signals-tile-skeleton__favicon" />
                    <span className="ai-signals-tile-skeleton__name" />
                    <span className="ai-signals-tile-skeleton__status" />
                  </div>
                </div>
              ))
            : socialSources.map((source) => (
            <div key={source.platform} className="tile-box">
              <div className="tile-box__box">
                <img
                  src={`https://favicon.is/${source.domain}`}
                  width={24}
                  height={24}
                  alt=""
                  className="ai-signals-results__tile-favicon"
                />
                <span className="ai-signals-results__tile-name">{source.name}</span>
                <span
                  className={`ai-signals-results__tile-status ai-signals-results__tile-status--${
                    source.connected ? 'on' : 'off'
                  }`}
                >
                  {t(
                    source.connected
                      ? 'aiSignals.results.sources.added'
                      : 'aiSignals.results.sources.notAdded'
                  )}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="stats stats--two-cols">
        <div className="ai-signals-results__panel">
          <div className="page-box__title-action">
            <div className="page-box__title page-box__title--flex">
              {loadingData ? <TitleSkeleton /> : t('aiSignals.results.mentions.title')}
            </div>
          </div>
          <div className="stats-box ai-signals-results__mentions-box">
            {loadingData ? (
              <SectionSkeleton />
            ) : (
              <>
            {mentionsScore.total > 0 &&
            (mentionsScore.score / mentionsScore.total) * 100 > MENTIONS_SCORE_GREEN_THRESHOLD ? (
              <div className="playbook-stat__celebrate ai-signals-results__celebrate" aria-hidden="true">
                <span className="playbook-stat__celebrate-spark playbook-stat__celebrate-spark--1" />
                <span className="playbook-stat__celebrate-spark playbook-stat__celebrate-spark--2" />
                <span className="playbook-stat__celebrate-spark playbook-stat__celebrate-spark--3" />
                <span className="playbook-stat__celebrate-spark playbook-stat__celebrate-spark--4" />
                <div className="playbook-stat__celebrate-circle">
                  <span className="ico--tick ico--md" />
                </div>
              </div>
            ) : (
              <div
                className="playbook-stat__celebrate ai-signals-results__celebrate ai-signals-results__celebrate--warning"
                aria-hidden="true"
              >
                <span className="playbook-stat__celebrate-spark playbook-stat__celebrate-spark--1" />
                <span className="playbook-stat__celebrate-spark playbook-stat__celebrate-spark--2" />
                <span className="playbook-stat__celebrate-spark playbook-stat__celebrate-spark--3" />
                <span className="playbook-stat__celebrate-spark playbook-stat__celebrate-spark--4" />
                <div className="playbook-stat__celebrate-circle">
                  <span className="ico--warning ico--md" />
                </div>
              </div>
            )}
            <div className="stats-box__content">
              <div className="ai-signals-results__score">
                <span className="ai-signals-results__score-value">{mentionsScore.score}</span>
                <span className="ai-signals-results__score-max">
                  {t('aiSignals.results.mentions.of')} {mentionsScore.total}
                </span>
              </div>
              <span className="ai-signals-results__trend">
                <TrendTag trendValue={mentionsScore.trend} suffix="%" size="MD" />
              </span>
              <p className="ai-signals-results__desc">{t('aiSignals.results.mentions.desc')}</p>
            </div>
              </>
            )}
          </div>
        </div>

        <div className="ai-signals-results__panel">
          <div className="page-box__title-action">
            <div className="page-box__title page-box__title--flex">
              {loadingData ? <TitleSkeleton /> : t('aiSignals.results.competitors.title')}
            </div>
          </div>
          {loadingData ? (
            <SectionSkeleton bordered />
          ) : (
          <DataTable
            value={competitorMetrics}
            stripedRows
            className="table-wrapper table-wrapper--stack"
            responsiveLayout="stack"
            tableClassName="table"
          >
            <Column
              field="brand"
              header={t('aiSignals.results.competitors.brands')}
              headerClassName="table__header"
              bodyClassName="table__cell--stack-title"
            />
            <Column
              header={t('aiSignals.results.competitors.mentions')}
              headerClassName="table__header table__header--center"
              bodyClassName="table__cell--center"
              body={(row) => (row.mentions ? row.mentions : '-')}
            />
            <Column
              header={t('aiSignals.results.competitors.backlinks')}
              headerClassName="table__header table__header--center"
              bodyClassName="table__cell--center"
              body={(row) => (row.aiBacklinks ? row.aiBacklinks : '-')}
            />
            <Column
              header={t('aiSignals.results.competitors.sentiment')}
              headerClassName="table__header table__header--center"
              bodyClassName="table__cell--center"
              body={(row) =>
                row.sentiment == null ? (
                  '-'
                ) : (
                  <span
                    className={`ai-signals-results__sentiment ai-signals-results__sentiment--${row.tone}`}
                  >
                    {row.sentiment}%
                  </span>
                )
              }
            />
          </DataTable>
          )}
        </div>
      </div>

      <div className="stats stats--full-width ai-signals-results__chart">
        <div className="ai-signals-results__panel">
          <div className="page-box__title-action">
            <div className="page-box__title page-box__title--flex">
              {loadingData ? <TitleSkeleton /> : t('aiSignals.results.trend.title')}
            </div>
          </div>
          {loadingData ? (
            <SectionSkeleton bordered />
          ) : (
          <div className="stats-box">
            <div className="stats-box__content">
              {trendMatrix.length > 1 && (
                <LineChart
                  firstData={trendMatrix}
                  headers={trendMatrix[0].slice(1) as string[]}
                  legendType="FLEX_START"
                  legendPosition="bottom"
                  autoFitYAxis
                  minValue={0}
                  smooth
                />
              )}
            </div>
          </div>
          )}
        </div>
      </div>

      {(loadingData || sourceDomains.length > 0) && (
      <div className="stats stats--full-width ai-signals-results__chart">
        <div className="stats__panel domains-per-model">
          <div className="page-box__title-action">
            <div className="page-box__title page-box__title--flex">
              {loadingData ? (
                <TitleSkeleton />
              ) : (
                <>
                  {t('aiSignals.results.domains.title')}
                  <CustomTooltip tooltipText="aiSignals.results.domains.tooltip" addMargin={false} />
                </>
              )}
            </div>
          </div>
          <div className="stats-box">
            <div className="stats-box__content">
              <Tooltip target=".ai-signals-results__bar-segments" />
              <SimpleBar
                className="domains-per-model__scroll-box"
                style={{ maxWidth: '100%' }}
                autoHide={false}
              >
                <div className="domains-per-model__content">
                  {loadingData ? <SectionSkeleton bare /> : sourceDomains.map((entry) => {
                    const meta = MODEL_META[entry.model] ?? { name: entry.model, icon: '' };
                    const maxCount = Math.max(...entry.domains.map((d) => d.count), 1);
                    return (
                      <div key={entry.model} className="domains-per-model__column">
                        <div className="domains-per-model__col-header">
                          <div className="domains-per-model__col-title">
                            <span className={`ico--sm ${meta.icon}`} />
                            <span className="domains-per-model__col-name">{meta.name}</span>
                          </div>
                          <span className="domains-per-model__col-total">
                            {entry.total} {t('total')}
                          </span>
                        </div>
                        <div className="domains-per-model__col-rows">
                          {entry.domains.map((d) => {
                            const barWidth = Math.round((d.count / maxCount) * 100);
                            return (
                              <div key={d.domain} className="domains-per-model__row">
                                <div className="domains-per-model__bar-row">
                                  <div className="domains-per-model__bar-track">
                                    <div
                                      className="domains-per-model__bar-fill"
                                      style={{
                                        width: `${barWidth}%`,
                                        backgroundColor: 'var(--primary-ux-500)',
                                      }}
                                      data-pr-tooltip={`${d.brandMentions} ${t(
                                        'aiSignals.results.competitors.brands'
                                      )}`}
                                      data-pr-position="top"
                                    />
                                  </div>
                                  <span className="domains-per-model__count">{d.count}</span>
                                </div>
                                <div className="domains-per-model__domain-row">
                                  <img
                                    src={`https://favicon.is/${d.domain}`}
                                    width={16}
                                    height={16}
                                    alt=""
                                    className="domains-per-model__favicon"
                                  />
                                  <span className="domains-per-model__domain-name">{d.domain}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </SimpleBar>
            </div>
          </div>
        </div>
      </div>
      )}

      {(loadingData || topSources.rows.length > 0) && (
      <div className="stats stats--full-width ai-signals-results__chart">
        <div className="stats__panel ai-signals-sources">
          <div className="page-box__title-action">
            <div className="page-box__title page-box__title--flex">
              {loadingData ? (
                <TitleSkeleton />
              ) : (
                <>
                  {t('aiSignals.results.sourcesPerModel.title')}
                  <CustomTooltip
                    tooltipText="aiSignals.results.sourcesPerModel.note"
                    addMargin={false}
                    direction="bottom"
                  />
                </>
              )}
            </div>
          </div>
          <SimpleBar style={{ maxWidth: '100%' }} autoHide={false}>
            <div className="ai-signals-sources__grid">
              {loadingData ? <SectionSkeleton bordered /> : topSources.rows.map((source) => {
                const maxCount = Math.max(...source.counts, 1);
                return (
                  <div key={source.domain} className="ai-signals-sources__card">
                    <img
                      src={`https://favicon.is/${source.domain}`}
                      width={40}
                      height={40}
                      alt=""
                      className="ai-signals-sources__favicon"
                    />
                    <div className="ai-signals-sources__domain">{source.domain}</div>
                    <div className="ai-signals-sources__count">
                      <span className="ai-signals-sources__count-value">{source.total}</span>
                      <span className="ai-signals-sources__count-label">{t('mentions')}</span>
                    </div>
                    <div className="ai-signals-sources__bars">
                      {topSources.models.map((model, i) => (
                        <div key={model.model} className="ai-signals-sources__bar-col">
                          <div className="ai-signals-sources__bar-area">
                            <div
                              className="ai-signals-sources__bar"
                              style={{ height: `${Math.round((source.counts[i] / maxCount) * 100)}%` }}
                            />
                          </div>
                          <span className={`ico--xs ${model.icon}`} />
                          <span className="ai-signals-sources__bar-count">{source.counts[i]}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </SimpleBar>
        </div>
      </div>
      )}

      {(loadingData || contextWords.length > 0) && (
        <div className="stats stats--full-width ai-signals-results__chart">
          <div className="stats__panel ai-signals-cloud">
            <div className="page-box__title-action">
              <div className="page-box__title page-box__title--flex">
                {loadingData ? (
                  <TitleSkeleton />
                ) : (
                  <>
                    {t('aiSignals.results.context.title')}
                    <CustomTooltip tooltipText="aiSignals.results.context.tooltip" addMargin={false} />
                  </>
                )}
            </div>
          </div>
          {loadingData ? (
            <SectionSkeleton bordered />
          ) : (
          <div className="stats-box">
            <button
              type="button"
              className="ai-signals-cloud__toggle"
              onClick={() => setShowSentiment((value) => !value)}
            >
              <span className={`ico--xs ${showSentiment ? 'ico--eye' : 'ico--eye-off'}`} />
              <span>
                {t(
                  showSentiment
                    ? 'aiSignals.results.context.hideSentiment'
                    : 'aiSignals.results.context.showSentiment'
                )}
              </span>
            </button>
            <div className="stats-box__content">
              <Tooltip target=".ai-signals-cloud__word" position="top" />
              <div className="ai-signals-cloud__words">
                {contextWords.map((word) => (
                  <span
                    key={word.text}
                    className="ai-signals-cloud__word"
                    style={{
                      fontSize: `${WORD_FONT_SIZE[word.weight]}px`,
                      color: getWordColor(word.weight, word.sentiment, showSentiment),
                    }}
                    data-pr-tooltip={t('aiSignals.results.context.share', { share: word.share })}
                    data-pr-position="top"
                  >
                    {word.text}
                  </span>
                ))}
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
      )}

      <div className="stats stats--full-width ai-signals-results__chart">
        <div className="stats__panel ai-signals-topics">
          <div className="page-box__title-action">
            <div className="page-box__title page-box__title--flex">
              {loadingData ? (
                <TitleSkeleton />
              ) : (
                <>
                  {t('aiSignals.results.topics.title')}
                  <CustomTooltip
                    tooltipText="aiSignals.results.topics.subtitle"
                    addMargin={false}
                    direction="bottom"
                  />
                </>
              )}
            </div>
          </div>
          {loadingData ? (
            <SectionSkeleton bordered />
          ) : (
          <DataTable
            value={discussionTopics}
            stripedRows
            className="table-wrapper table-wrapper--stack"
            responsiveLayout="stack"
            tableClassName="table"
          >
            <Column
              field="name"
              header={t('aiSignals.results.topics.topic')}
              headerClassName="table__header"
              bodyClassName="table__cell--stack-title"
              body={(row) => <span className="ai-signals-topics__name">{row.name}</span>}
            />
            <Column
              header={t('aiSignals.results.topics.sentiment')}
              headerClassName="table__header"
              body={(row) => renderSentiment(row.tone)}
            />
            <Column
              field="mentions"
              header={t('aiSignals.results.topics.mentions')}
              headerClassName="table__header table__header--center"
              bodyClassName="table__cell--center"
            />
            {isOwnMode && (
              <Column
                headerClassName="table__header"
                bodyClassName="ai-signals-topics__action-cell table__cell--stack-wide"
                body={(row) => {
                  const tag = projectTags.find((t) => t.label === row.name);
                  return tag ? (
                    <button
                      className="btn btn--sm btn--gray"
                      type="button"
                      onClick={() => handleRemoveTopicTag(tag.id)}
                    >
                      <span className="ico--trash ico--xs" />
                      {t('aiSignals.results.topics.removeTag')}
                    </button>
                  ) : (
                    <button
                      className="btn btn--sm btn--secondary"
                      type="button"
                      onClick={() => handleAddTopicToTag(row.name)}
                    >
                      <span className="ico--plus ico--xs" />
                      {t('aiSignals.results.topics.addToTags')}
                    </button>
                  );
                }}
              />
            )}
          </DataTable>
          )}
        </div>
      </div>

      {(loadingData || backlinks.length > 0) && (
      <div className="stats stats--full-width ai-signals-results__chart">
        <div className="stats__panel ai-signals-backlinks">
          <div className="page-box__title-action">
            <div className="page-box__title page-box__title--flex">
              {loadingData ? <TitleSkeleton /> : t('aiSignals.results.backlinks.title')}
            </div>
          </div>
          {loadingData ? (
            <SectionSkeleton bordered />
          ) : (
          <DataTable
            value={backlinks}
            stripedRows
            scrollable
            className="table-wrapper table-wrapper--stack"
            responsiveLayout="stack"
            tableClassName="table"
          >
            <Column
              header={t('aiSignals.results.backlinks.source')}
              headerClassName="table__header"
              bodyClassName="table__cell--stack-title"
              body={(row) => (
                <span className="ai-signals-backlinks__source">
                  <img
                    src={`https://favicon.is/${row.source}`}
                    width={16}
                    height={16}
                    alt=""
                    className="ai-signals-backlinks__favicon"
                  />
                  {row.source}
                </span>
              )}
            />
            <Column
              field="mentions"
              header={t('aiSignals.results.backlinks.mentions')}
              headerClassName="table__header table__header--center"
              bodyClassName="table__cell--center"
            />
            <Column
              header={t('aiSignals.results.backlinks.models')}
              headerClassName="table__header"
              body={(row) => (
                <span className="ai-signals-backlinks__models">
                  {row.models.map((model: string, i: number) => (
                    <span key={i} className={`${MODEL_META[model]?.icon ?? ''} ico--sm`} />
                  ))}
                </span>
              )}
            />
            <Column
              header={t('aiSignals.results.backlinks.reach')}
              headerClassName="table__header table__header--center"
              bodyClassName="table__cell--center"
              body={(row) => formatCompact(row.reach)}
            />
            <Column
              header={t('aiSignals.results.backlinks.sentiment')}
              headerClassName="table__header table__header--center"
              bodyClassName="table__cell--center"
              body={(row) => renderSentiment(row.tone)}
            />
          </DataTable>
          )}
        </div>
      </div>
      )}
    </div>
  );

  const renderActivation = () => (
    <div className="ai-signals-activation">
      <div className="ai-signals-activation__header">
        <span className="ai-signals-activation__spinner" />
        <div>
          <h1 className="ai-signals-activation__title">{t('aiSignals.activation.title')}</h1>
          <p className="ai-signals-activation__subtitle">{t('aiSignals.activation.subtitle')}</p>
        </div>
      </div>
      <div className="ai-signals-activation__steps">
        {ACTIVATION_STEPS.map((stepKey, index) => {
          const isComplete = index < activeStep;
          const isActive = index === activeStep;
          const state = isComplete ? 'complete' : isActive ? 'active' : 'pending';
          return (
            <div
              key={stepKey}
              className={`ai-signals-activation__step ai-signals-activation__step--${state}`}
            >
              <span className="ai-signals-activation__badge">
                {isComplete ? <i className="ico--tick ico--xs" /> : index + 1}
              </span>
              <span className="ai-signals-activation__step-label">{t(stepKey)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderOffer = () => (
    <div className="ai-signals">
      {/* Telefon: tytuł i „dodaj projekt” w górnym pasku, na stronie tylko etykieta i opis. */}
      <MobilePageHeader
        title={<span>{t('aiSignals.title')}</span>}
        actions={
          isBrand24Connected && (
            <button
              className="btn btn--sm mobile-header__add"
              type="button"
              onClick={() => setShowPicker(true)}
              aria-label={t('aiSignals.addProject')}
            >
              <span className="ico--add ico--xs" />
            </button>
          )
        }
      />
      <div className="ai-signals__header">
        <div className="ai-signals__header-main">
          <div className="title-box__row">
            <div className="title-box__row-title">
              {!isMobile && t('aiSignals.title')}
              <span className="ai-signals__badge">{t('aiSignals.badge')}</span>
            </div>
          </div>
          <div className="title-box__subtitle">{t('aiSignals.subtitle')}</div>
        </div>
        {isBrand24Connected && !isMobile && (
          <button className="btn btn--sm" type="button" onClick={() => setShowPicker(true)}>
            {t('aiSignals.addProject')}
          </button>
        )}
      </div>

      {!isBrand24Connected && (
        <div className="ai-signals__section ai-signals__price">
          <div>
            <div className="ai-signals__label">{t('aiSignals.priceLabel')}</div>
            <div className="ai-signals__price-value">
              <span className="ai-signals__price-amount">{t('aiSignals.price')}</span>
              <span className="ai-signals__price-period">{t('aiSignals.pricePeriod')}</span>
            </div>
            <div className="ai-signals__price-note">{t('aiSignals.priceNote')}</div>
          </div>
          <div className="ai-signals__price-action">
            <button className="btn btn--sm" type="button" onClick={() => navigate(INTEGRATIONS)}>
              {t('aiSignals.activate')}
            </button>
            <div className="ai-signals__contact">{t('aiSignals.contact')}</div>
          </div>
        </div>
      )}

      <div className="ai-signals__section">
        <div className="ai-signals__label">{t('aiSignals.included')}</div>

        <div className="ai-signals__highlight">
          <div>
            <div className="ai-signals__feature-heading">
              <span className="ai-signals__feature-title">{t('aiSignals.highlight.title')}</span>
            </div>
            <p className="ai-signals__feature-desc">{t('aiSignals.highlight.desc')}</p>
          </div>
        </div>

        <div className="ai-signals__features">
          {FEATURES.map((feature) => (
            <div key={feature.titleKey} className="ai-signals__feature">
              <div className="ai-signals__feature-icon">
                <i className={`${feature.icon} ico--sm`} />
              </div>
              <div>
                <div className="ai-signals__feature-title">{t(feature.titleKey)}</div>
                <p className="ai-signals__feature-desc">{t(feature.descKey)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="ai-signals__footer">{t('aiSignals.footer')}</div>
    </div>
  );

  // Until we know whether the project is linked, render nothing — this avoids flashing the "connect" card
  // for a linked project without showing a results skeleton for an unlinked one. Once resolved, the linked
  // view drives its own per-section skeletons via loadingData.
  if (checkingLink && !isActivated) {
    return null;
  }

  // A linked project (or a just-finished activation) skips the offer panel and shows the results view.
  if (isActivated || isProjectLinked) {
    return (
      <div className="ai-signals-page page-content">
        <div className="page-box">
          {/* Telefon: tytuł, kalendarz i odłączenie w górnym pasku (kalendarz jak w AI Ads). */}
          <MobilePageHeader
            title={<span>{t('aiSignals.results.header')}</span>}
            actions={
              <>
                <FilterPanel baseUrl={location.pathname} showChatTypeDropdown={false} showPeriodTabs={false} />
                <button
                  className="btn btn--sm btn--red mobile-header__add"
                  type="button"
                  onClick={handleUnlink}
                  aria-label={t('aiSignals.unlink')}
                >
                  <span className="ico--trash ico--xs" aria-hidden="true" />
                </button>
              </>
            }
          />
          {!isMobile && (
          <div className="page-box__filters" data-observe-sticky>
            <Title
              isBaseCategory={false}
              title={
                <div className="ai-signals-page__heading">
                  <span className="ai-signals-page__heading-title">
                    {t('aiSignals.results.header')}
                  </span>
                  <span className="title-box__subtitle">{t('aiSignals.results.subtitle')}</span>
                </div>
              }
              showStatusDot={true}
              children={
                <div className="filters">
                  <FilterPanel
                    baseUrl={location.pathname}
                    showChatTypeDropdown={false}
                    showPeriodTabs={false}
                  />
                  <button
                    id="ai-signals-unlink-btn"
                    className="action-btn action-btn--danger"
                    type="button"
                    onClick={handleUnlink}
                    aria-label={t('aiSignals.unlink')}
                  >
                    <span className="ico--trash ico--xs" aria-hidden="true" />
                  </button>
                  <CustomTooltip tooltipText="aiSignals.unlink" targetRef="ai-signals-unlink-btn" />
                </div>
              }
            />
          </div>
          )}
          <div className="page-box__stats">{renderResults()}</div>
        </div>
      </div>
    );
  }

  if (isActivating) {
    return (
      <div className="page-content page-content--ai-signals-activation">{renderActivation()}</div>
    );
  }

  return (
    <div className="page-content page-content--ai-signals">
      <div className="page-box">
        <div className="page-box__stats">{renderOffer()}</div>
      </div>

      <Brand24ProjectConfigDialog
        visible={showPicker}
        onHide={() => setShowPicker(false)}
        chatbeatProjectId={Number(projectId)}
        isOwnMode={isOwnMode}
        onSubmit={handleConfigure}
      />
    </div>
  );
};

export { AiSignalsPage };
export default AiSignalsPage;
